/**
 * Image caching utility using localStorage
 * Stores images as base64 data URLs for faster loading
 */

/** Inline SVG fallback so img onError does not depend on external hosts (e.g. via.placeholder.com). */
export const BROKEN_IMAGE_PLACEHOLDER_SRC =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="#eef1f8" width="400" height="300"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="system-ui,sans-serif" font-size="15">Image unavailable</text></svg>'
  );

const CACHE_PREFIX = 'property_image_cache_';
const CACHE_VERSION = '1';
const MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB max cache size
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB max per image

/**
 * Generate a cache key from image URL
 * @param {string} imageUrl - The image URL
 * @returns {string} Cache key
 */
const getCacheKey = (imageUrl) => {
  if (!imageUrl) return null;
  // Create a hash-like key from URL
  const urlHash = imageUrl.split('/').pop().split('?')[0];
  return `${CACHE_PREFIX}${CACHE_VERSION}_${urlHash}`;
};

/**
 * Get cache size in bytes
 * @returns {number} Total cache size
 */
const getCacheSize = () => {
  let totalSize = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX)) {
      const value = localStorage.getItem(key);
      if (value) {
        totalSize += new Blob([value]).size;
      }
    }
  }
  return totalSize;
};

/**
 * Clean old cache entries if cache is too large
 * Removes oldest entries first
 */
const cleanCache = () => {
  const cacheSize = getCacheSize();
  if (cacheSize <= MAX_CACHE_SIZE) return;

  // Get all cache entries with timestamps
  const entries = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX)) {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          const data = JSON.parse(value);
          entries.push({
            key,
            timestamp: data.timestamp || 0,
            size: new Blob([value]).size
          });
        } catch (e) {
          // If not JSON, treat as old format and remove
          entries.push({
            key,
            timestamp: 0,
            size: new Blob([value]).size
          });
        }
      }
    }
  }

  // Sort by timestamp (oldest first)
  entries.sort((a, b) => a.timestamp - b.timestamp);

  // Remove oldest entries until under limit
  let currentSize = cacheSize;
  for (const entry of entries) {
    if (currentSize <= MAX_CACHE_SIZE * 0.8) break; // Remove until 80% of max
    localStorage.removeItem(entry.key);
    currentSize -= entry.size;
  }
};

/**
 * Store image in cache
 * @param {string} imageUrl - The image URL
 * @param {string} dataUrl - Base64 data URL or blob URL
 * @returns {boolean} Success status
 */
export const storeImageInCache = async (imageUrl, dataUrl) => {
  try {
    if (!imageUrl || !dataUrl) return false;

    // Skip if already a blob URL (we'll convert it)
    if (dataUrl.startsWith('blob:')) {
      // Convert blob URL to base64
      try {
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        
        // Check size
        if (blob.size > MAX_IMAGE_SIZE) {
          console.warn('Image too large to cache:', blob.size);
          return false;
        }

        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const base64DataUrl = await base64Promise;
        const cacheKey = getCacheKey(imageUrl);
        
        if (cacheKey) {
          const cacheData = {
            dataUrl: base64DataUrl,
            timestamp: Date.now(),
            url: imageUrl
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          cleanCache();
          return true;
        }
      } catch (error) {
        console.error('Error converting blob to base64:', error);
        return false;
      }
    } else if (dataUrl.startsWith('data:')) {
      // Already a data URL, store directly
      const cacheKey = getCacheKey(imageUrl);
      if (cacheKey) {
        // Check size
        const sizeEstimate = dataUrl.length * 0.75; // Base64 is ~33% larger
        if (sizeEstimate > MAX_IMAGE_SIZE) {
          console.warn('Image too large to cache:', sizeEstimate);
          return false;
        }

        const cacheData = {
          dataUrl: dataUrl,
          timestamp: Date.now(),
          url: imageUrl
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        cleanCache();
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Error storing image in cache:', error);
    return false;
  }
};

/**
 * Get image from cache
 * @param {string} imageUrl - The image URL
 * @returns {string|null} Cached data URL or null
 */
export const getImageFromCache = (imageUrl) => {
  try {
    if (!imageUrl) return null;

    const cacheKey = getCacheKey(imageUrl);
    if (!cacheKey) return null;

    const cachedData = localStorage.getItem(cacheKey);
    if (!cachedData) return null;

    try {
      const data = JSON.parse(cachedData);
      // Check if cache entry is valid (not older than 7 days)
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
      if (Date.now() - data.timestamp > maxAge) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      return data.dataUrl;
    } catch (e) {
      // Old format, return as-is
      return cachedData.startsWith('data:') ? cachedData : null;
    }
  } catch (error) {
    console.error('Error getting image from cache:', error);
    return null;
  }
};

/**
 * Remove image from cache
 * @param {string} imageUrl - The image URL
 */
export const removeImageFromCache = (imageUrl) => {
  try {
    const cacheKey = getCacheKey(imageUrl);
    if (cacheKey) {
      localStorage.removeItem(cacheKey);
    }
  } catch (error) {
    console.error('Error removing image from cache:', error);
  }
};

/**
 * Clear all cached images
 */
export const clearImageCache = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing image cache:', error);
  }
};
